import * as _ from 'lodash';
import * as hash from 'object-hash';
import { MutableRefObject, useContext, useEffect, useReducer, useState } from 'react';
import { Dispatch, connect } from "react-redux";
import { IBaseResponse, IRecipientProfileHlaInfoPost } from '@nkr/models/index';
import { IApplicationState, AppActionCreators, IAppState, RecipientActionCreators, IRecipientProfileHlaInfoResponse } from '@nkr/store/index';
import { Alert, Button, Col, ControlLabel, FormControl, HelpBlock, FormGroup, Label, Modal, Row, Well } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ProfileWrapper } from '../styles';
import { RecipientProfileContext } from '../models';
import { hlaReducer, initialState } from './state';
import { ILookupData } from '../lookup-data';
import HLAClass1 from './hla-class-1';
import HLAClass2 from './hla-class-2';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';

interface IOwnProps {
    saveRef: MutableRefObject<any>,
    afterRef: MutableRefObject<any>,
    lookupData: ILookupData
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getHlaInfoData: (centerId: number, recipientId: number) => Promise<IRecipientProfileHlaInfoResponse>,
    postHlaInfo: (centerId: number, recipientId: number, data: IRecipientProfileHlaInfoPost) => Promise<IBaseResponse>,
    postAutoPopulateHLA: (recipientId: number, centerId: number) => Promise<IBaseResponse>,
    postConfirmHLADiscrepancy: (recipientId: number, centerId: number, initials: string) => Promise<IBaseResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

const HlaInfoTab = (props: TProps) => {
    const context = useContext(RecipientProfileContext);
    const [state, dispatch] = useReducer(hlaReducer, initialState);
    const [showHLADiscrepancyModal, setShowHLADiscrepancyModal] = useState(false);
    const [showSavedAlert, setShowSavedAlert] = useState(false);
    const [coordInitials, setCoordInitials] = useState<string>('');
    const [showDuplicateRecord, setShowDuplicateRecord] = useState(false);
    const [duplicateMessage, setDuplicateMessage] = useState<string>('');


    const handleDuplicateError = (error: any): boolean => {
        if (error.response.status != 409) {
            return false;
        }

        setDuplicateMessage(error.response.data);
        setShowDuplicateRecord(true);

        return true;
    };


    const handleCloseDuplicateRecord = () => {
        setShowDuplicateRecord(false);
    };


    const handleShowHLADiscrepancyModal = () => {
        setShowHLADiscrepancyModal(true);
    }

    const handleCoordInitial = (e: React.FormEvent<FormControl & HTMLInputElement>) => {

        let value = e.currentTarget.value;
        let alphaValue = value.replace(/[^A-Za-z]/g, '');

        setCoordInitials(alphaValue.toUpperCase());
    };


    const handleCloseHLADiscrepancyModal = () => {
        setShowHLADiscrepancyModal(false);
        setCoordInitials('');
    }

    const checkValid = (): boolean => {
        return coordInitials.trim().length > 1;
    }

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        props.showLoading('Getting hla data, one moment please...').then(() => {
            props.getHlaInfoData(context.centerId, context.recipientId)
                .then((response) => {
                    if (response.data.success) {
                        let data = response.data.results;

                        dispatch({
                            type: 'afterLoad',
                            value: {
                                data,
                                hash: hash(data),
                            }
                        });
                    }
                })
                .catch(error => {
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleAutoPopulateHLA = () => {
        dispatch({
            type: 'toggleModal',
            value: !state.showModal
        });

        props.showLoading('HLA autopopulating, one moment please...').then(() => {
            props.postAutoPopulateHLA(context.recipientId, context.centerId)
                .then(response => {
                    if (response.data.success) {
                        getData();
                        toast.success('Autopopulate was successful');
                    }
                })
                .catch(error => {
                    if (handleDuplicateError(error)) {
                        return;
                    }

                    toast.error('Unable to update recipient');
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };


    const handleHLADiscrepancy = () => {
        setShowHLADiscrepancyModal(false);

        props.showLoading('Updating HLA record, one moment please...').then(() => {
            props.postConfirmHLADiscrepancy(context.recipientId, context.centerId, coordInitials)
                .then(response => {
                    if (response.data.success) {

                        context.updateHasChanges(false);
                        context.refreshHeader();

                        getData();
                    }
                })
                .catch(error => {
                    toast.error(error.response.data, { autoClose: 5000 });
                })
                .finally(() => {
                    props.hideLoading();
                    toast.success('Updating HLA record was successful');
                    setShowSavedAlert(true);
                });
        });
    };


    const handleHlaChange = (name : string, value : string | number | boolean | null) => {
        let data        = _.cloneDeep(state.data);
        data[name]      = value;

        let newHash     = hash(data);
        var hasChanged  = state.hash != newHash;

        dispatch({
            type: 'updateData',
            value: data
        });

        context.updateHasChanges(hasChanged);
    }


    const handleSave = () => {
        let { data } = state

        let postData: IRecipientProfileHlaInfoPost = {
            hlaA1: data.hlaA1,
            hlaA2: data.hlaA2,
            hlaB1: data.hlaB1,
            hlaB2: data.hlaB2,
            hlaBw1: data.hlaBw1,
            hlaBw2: data.hlaBw2,
            hlaC1: data.hlaC1,
            hlaC2: data.hlaC2,
            hlaDrb1: data.hlaDrb1,
            hlaDrb2: data.hlaDrb2,
            hlaHasDr52: data.hlaHasDr52,
            hlaHasDr53: data.hlaHasDr53,
            hlaHasDr51: data.hlaHasDr51,
            hlaDqa1: data.hlaDqa1,
            hlaDqa2: data.hlaDqa2,
            hlaDqb1: data.hlaDqb1,
            hlaDqb2: data.hlaDqb2,
            hlaDpb1: data.hlaDpb1,
            hlaDpb2: data.hlaDpb2,
            labReviewDate: data.labReviewDate,
            labReviewComplete: data.labReviewComplete,
            labReviewInitials: data.labReviewInitials
        }

        if (data.labReviewDate && !data.labReviewInitials) {
            toast.error('Your initials are required to sign off the HLA lab review');
            return;
        }

        props.showLoading('Saving HLA data, one moment please...').then(() => {
            props.postHlaInfo(context.centerId, context.recipientId, postData)
                .then(response => {
                    if (response.data.success) {
                        dispatch({
                            type: 'updateHash',
                            value: hash(state.data)
                        });

                        context.updateHasChanges(false);
                        context.refreshHeader();

                        toast.success('HLA info updated successfully', {
                            onClose: () => {
                                context.doAfterAction(props.afterRef.current);
                                getData();
                            }
                        })
                    }
                })
                .catch(error => {
                    if (handleDuplicateError(error)) {
                        return;
                    }

                    toast.error(error.response?.data || 'Unable to update recipient HLA info', { autoClose: 5000 });
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleToggleModal = () => {
        dispatch({
            type: 'toggleModal',
            value: !state.showModal
        });
    };

    context.registerSave(props.saveRef, handleSave);

    let { data } = state;

    if (!data || !props.lookupData || props.lookupData.hlaValueList.length == 0 || !context.headerInfo) {
        return <></>
    }

    let hasAllele = state.data?.hasAlleleData ?? false;

    const aAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'A');
    const a2Antigens = props.lookupData.hlaValueList.find(x => x.hla == 'A2');
    const bAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'B');
    const b2Antigens = props.lookupData.hlaValueList.find(x => x.hla == 'B2');
    const bwAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'Bw');
    const cwAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'Cw');

    const drAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'DR');
    const dr2Antigens = props.lookupData.hlaValueList.find(x => x.hla == 'DR2');
    const dqAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'DQ');
    const dqaAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'DQa');
    const dpAntigens = props.lookupData.hlaValueList.find(x => x.hla == 'DP');

    const antigenMap = [
        { label: 'Antigen A-1', name: 'hlaA1', value: data.hlaA1, list: aAntigens.antigens, selectValue: 0, selectLabel: '- Select -' },
        { label: 'Antigen A-2', name: 'hlaA2', value: data.hlaA2, list: a2Antigens.antigens, selectValue: 0, selectLabel: '- Select -' },

        { label: 'Antigen B-1', name: 'hlaB1', value: data.hlaB1, list: bAntigens.antigens, selectValue: 0, selectLabel: '- Select -' },
        { label: 'Antigen B-2', name: 'hlaB2', value: data.hlaB2, list: b2Antigens.antigens, selectValue: 0, selectLabel: '- Select -' },

        { label: 'Antigen DR-1', name: 'hlaDrb1', value: data.hlaDrb1, list: drAntigens.antigens, selectValue: 0, selectLabel: '- Select -' },
        { label: 'Antigen DR-2', name: 'hlaDrb2', value: data.hlaDrb2, list: dr2Antigens.antigens, selectValue: 0, selectLabel: '- Select -' },

        { label: 'Antigen Bw-1', name: 'hlaBw1', value: data.hlaBw1, list: bwAntigens.antigens, selectValue: 0, selectLabel: '- Select -' },
        { label: 'Antigen Bw-2', name: 'hlaBw2', value: data.hlaBw2, list: bwAntigens.antigens, selectValue: -1, selectLabel: 'No 2nd Antigen' },

        { label: 'Antigen Cw-1', name: 'hlaC1', value: data.hlaC1, list: cwAntigens.antigens, selectValue: 0, selectLabel: '- Select -' },
        { label: 'Antigen Cw-2', name: 'hlaC2', value: data.hlaC2, list: cwAntigens.antigens, selectValue: -1, selectLabel: 'No 2nd Antigen' },

        { label: 'Antigen DQb-1', name: 'hlaDqb1', value: data.hlaDqb1, list: dqAntigens.antigens, selectValue: 0, selectLabel: '- Select -' },
        { label: 'Antigen DQb-2', name: 'hlaDqb2', value: data.hlaDqb2, list: dqAntigens.antigens, selectValue: -1, selectLabel: 'No 2nd Antigen' },

        { label: 'Antigen DQa-1', name: 'hlaDqa1', value: data.hlaDqa1, list: dqaAntigens.antigens, selectValue: 0, selectLabel: '- Select -' },
        { label: 'Antigen DQa-2', name: 'hlaDqa2', value: data.hlaDqa2, list: dqaAntigens.antigens, selectValue: -1, selectLabel: 'No 2nd Antigen' },

        { label: 'Antigen DPb-1', name: 'hlaDpb1', value: data.hlaDpb1, list: dpAntigens.antigens, selectValue: 0, selectLabel: '- Select -' },
        { label: 'Antigen DPb-2', name: 'hlaDpb2', value: data.hlaDpb2, list: dpAntigens.antigens, selectValue: -1, selectLabel: 'No 2nd Antigen' },
    ];

    const drAntigenMap = [
        { label: 'DR-51', name: 'hlaHasDr51', value: data.hlaHasDr51 != null ? (data.hlaHasDr51 == true ? 1 : 0) : -1 },
        { label: 'DR-52', name: 'hlaHasDr52', value: data.hlaHasDr52 != null ? (data.hlaHasDr52 == true ? 1 : 0) : -1 },
        { label: 'DR-53', name: 'hlaHasDr53', value: data.hlaHasDr53 != null ? (data.hlaHasDr53 == true ? 1 : 0) : -1 },
    ];

    let disableSubmit = !checkValid();

    return (
        <>
            <ProfileWrapper>
                <div className='container-fluid'>

                    <Row>
                        <Col xs={12}>
                            <Well style={{ paddingTop: 9.5, paddingBottom: 9.5 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: '12px',
                                    }}
                                >
                                    {/* Left side text */}
                                    <div style={{ flex: '1 1 auto', minWidth: '250px' }}>
                                        <FormControl.Static>
                                            All antigen information must be complete to be exportable
                                        </FormControl.Static>
                                    </div>

                                    {/* Button */}
                                    <div style={{ flex: '0 0 auto' }}>
                                        <Button bsSize='sm' className='wide_button' disabled={!hasAllele} onClick={handleToggleModal}>Auto-Populate HLA</Button>
                                    </div>

                                    {/* Legend section */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            flex: '1 1 auto',
                                            justifyContent: 'flex-end',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            fontSize: 12,
                                            gap: '10px',
                                        }}
                                    >

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <div
                                                style={{
                                                    height: 20,
                                                    width: 20,
                                                    backgroundColor: '#5bb85d',
                                                    border: '1px solid #ddd',
                                                    borderRadius: 4,
                                                }}
                                            />
                                            <div>- Matched Antigen</div>
                                        </div>


                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <div
                                                style={{
                                                    height: 20,
                                                    width: 20,
                                                    backgroundColor: '#FF954D',
                                                    border: '1px solid #FF954D',
                                                    borderRadius: 4,
                                                }}
                                            />
                                            <div>- Unexpected Antigen (overridden)</div>
                                        </div>


                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <div
                                                style={{
                                                    height: 20,
                                                    width: 20,
                                                    backgroundColor: '#C83333',
                                                    border: '1px solid #ddd',
                                                    borderRadius: 4,
                                                }}
                                            />
                                            <div>- Unexpected Antigen</div>
                                        </div>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <div
                                                style={{
                                                    height: 20,
                                                    width: 20,
                                                    backgroundColor: '#777777',
                                                    border: '1px solid #bbb',
                                                    borderRadius: 4,
                                                }}
                                            />
                                            <div>- Not Evaluated</div>
                                        </div>
                                    </div>
                                </div>
                            </Well>
                        </Col>
                    </Row>

                    {data.hasHlaDiscrepancy && !data.hlaDiscrepancyIsEquivalent && (
                        <Row>
                            <Col xs={12}>
                                <Alert bsSize="large" style={{
                                    marginBottom: 5,
                                    backgroundColor: '#f8d7da',
                                    borderColor: '#f5c6cb',
                                    color: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap'
                                }}>
                                    <p style={{ margin: 0, flex: 1 }}>
                                        <span style={{ fontSize: 17, fontWeight: 'bold', color: '#d35a52' }}>Alert:</span> We found a discrepancy in the serological equivalents determined from our high resolution uploads versus what you entered below. This could be an error or could result from an equivalent serological group. Please ensure that all HLA is reviewed and then confirm this data is correct.
                                    </p>
                                    <Button bsStyle="danger" bsSize="sm" disabled={!hasAllele} onClick={handleShowHLADiscrepancyModal} style={{ marginLeft: 10, whiteSpace: 'nowrap' }}>
                                        <div style={{ fontSize: 14 }}>Confirm Equivalent Serological Group</div>
                                    </Button>
                                </Alert>
                            </Col>
                        </Row>)
                    }

                    {showSavedAlert && (
                        <Row>
                            <Col xs={12}>
                                <Alert bsSize="large" style={{
                                    marginBottom: 5,
                                    backgroundColor: '#def0da',
                                    borderColor: '#9bc49b',
                                    color: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap'
                                }}>
                                    <p style={{ margin: 0, flex: 1 }}>
                                        <span style={{ fontSize: 17, fontWeight: 'bold', color: '#337034ff' }}>Saved:</span> HLA override confirmed and saved successfully
                                    </p>
                                </Alert>
                            </Col>
                        </Row>
                    )}

                    <Row style={{ display: 'flex', alignItems: 'stretch' }}>
                        <HLAClass1
                            data={data}
                            hlaValueList={props.lookupData.hlaValueList}
                            handleHlaChange={handleHlaChange}
                        />

                        <HLAClass2
                            data={data}
                            hlaValueList={props.lookupData.hlaValueList}
                            handleHlaChange={handleHlaChange}
                        />
                    </Row>

                    <Row style={{ marginBottom: 10, marginTop: 10 }}>
                        <Col md={12} className="text-center">
                            <br />
                            <h4 className='heading center' style={{ color: '#d68f0e' }}>
                                ANTIBODIES ARE SET IN THE <a href={`/center/${context.centerId}/recipient/${context.recipientId}/toolbox`}> TOOLBOX</a>
                            </h4>
                        </Col>
                    </Row>

                </div>
            </ProfileWrapper>

            <Modal backdrop='static' show={state.showModal} onHide={handleToggleModal}>
                <Modal.Header closeButton>
                    Please Confirm
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to use the <b>NKR High Resolution Tissue Typing</b> to add or overwrite antigen information?
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle='primary' onClick={handleAutoPopulateHLA}>Yes</Button>
                    <Button onClick={handleToggleModal}>No</Button>
                </Modal.Footer>
            </Modal>


            <Modal show={showHLADiscrepancyModal} bsSize='lg' backdrop='static' onHide={handleCloseHLADiscrepancyModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm HLA Discrepancy</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Please ensure that you have verified each HLA serological antigen
                        Once confirmed, if this error persists, it's likely due to an equivalent serological group creating a false flag, in which case you may sign off below.
                    </p>

                    <Alert style={{ background: '#e9ecef', borderColor: '#ced4da', color: 'black' }}>
                        I confirm that the HLA antigens listed are correct and that the discrepancy is a false flag likely due to an equivalent serological group.

                        <FormGroup style={{ marginTop: '0.8em', marginBottom: 0, marginRight: '0.5em' }}>
                            <ControlLabel>Authorizing Coordinator Initials</ControlLabel>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                                <FormControl
                                    type="text"
                                    value={coordInitials}
                                    maxLength={3}
                                    placeholder="Coordinator Initials"
                                    bsSize='sm'
                                    onChange={handleCoordInitial}
                                    style={{ width: '25%' }}
                                />

                                <Button
                                    bsStyle="danger"
                                    disabled={disableSubmit}
                                    onClick={handleHLADiscrepancy}
                                >
                                    Confirm False Flag
                                </Button>
                            </div>

                            <HelpBlock>At least two characters required.</HelpBlock>
                        </FormGroup>
                    </Alert>
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={handleCloseHLADiscrepancyModal}>Cancel</Button>
                </Modal.Footer>
            </Modal>

            <DuplicateRecordModal
                show={showDuplicateRecord}
                message={duplicateMessage}
                onClose={handleCloseDuplicateRecord}
            />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getHlaInfoData: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getHlaInfoData(centerId, recipientId));
        },
        postHlaInfo: (centerId, recipientId, data) => {
            return dispatch(RecipientActionCreators.postHlaInfo(centerId, recipientId, data));
        },
        postAutoPopulateHLA: (recipientId, centerId) => {
            return dispatch(RecipientActionCreators.postAutoPopulateHLA(recipientId, centerId))
        },
        postConfirmHLADiscrepancy: (recipientId, centerId, initials) => {
            return dispatch(RecipientActionCreators.postConfirmHLADiscrepancy(recipientId, centerId, initials))
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(HlaInfoTab);